import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react';
import { Alert, Spin } from '@arco-design/web-react';
import type { RuntimePrimaryNavigationItem } from '@jukun/platform-sdk/auth';
import { ApiError, errorMessage, subscribePlatformSessionInvalidation } from '../common/api/httpClient';
import { MobilePortalEntry, MobileApplicationProvider } from '../mobile/app/mobilePortalEntry';
import { useMobileViewport } from '../mobile/app/useMobileViewport';
import { MobileWorkbenchPage } from '../mobile/workbench/MobileWorkbenchPage';
import { PortalShell } from '../components/PortalShell';
import { PortalFormSessionProvider, useAuthenticatedFormSessionManager } from '../components/PortalFormSessionProvider';
import type { PortalFormSessionManager } from '../runtime/formSessionManager';
import { FormNavigationGuard, type FormNavigationTransaction } from '../runtime/formNavigationGuard';
import { TaskFormSelectionCoordinator, type PreparedTaskSelection, type TaskNavigationOptions } from '../modules/my-tasks/runtime/taskFormSelection';
import { MicroAppHost } from '../components/MicroAppHost';
import { PortalStatePage } from '../components/PortalStatePage';
import { usePortalOverlay } from '../components/PortalOverlayProvider';
import type { AuthSession, PortalApplication, PortalBootstrap } from '../model/portalModels';
import { authApi } from '../modules/auth/authApi';
import { canLoadFeishuSdk } from '../modules/auth/feishuEnvironment';
import { acquireFeishuCode } from '../modules/auth/feishuBridge';
import { hasFeishuWebCallback, hasFeishuWebStartHandoff } from '../modules/auth/feishuWebLogin';
import { withAbortTimeout } from '../modules/auth/feishuRecovery';
import { clearAccessTokens, applicationPermissionCodes, subscribeApplicationPermissions } from '../modules/auth/accessTokenManager';
import { LoginPage } from '../modules/auth/LoginPage';
import { portalApi } from '../modules/portal/portalApi';
import { ConstructionPage } from '../pages/ConstructionPage';
import { MyTasksPage } from '../modules/my-tasks/pages/MyTasksPage';
import { parseMyTasksRoute } from '../modules/my-tasks/runtime/myTasksRoute';
import { DraftsPage } from '../modules/drafts/pages/DraftsPage';
import { DraftFormSelectionCoordinator, type PreparedDraftSelection } from '../modules/drafts/runtime/draftFormSelection';
import { PortalOperationListPage } from '../modules/operations/pages/PortalOperationListPage';
import { PortalWorkbenchPage } from '../modules/workbench/pages/PortalWorkbenchPage';
import { PortalPersonalPage } from '../modules/personal/pages/PortalPersonalPage';
import {
  findFirstEnabledApplication,
  findRoutedApplication,
  isApplicationPathname,
  resolveApplicationRuntimeState,
  resolveApplicationSwitchPathname,
  resolveCurrentApplication,
} from '../navigation/applicationSelection';
import {
  defaultPortalPath,
  isFixedPortalPath,
  isPortalApplicationPath,
  normalizePortalPath,
  parsePortalOperationPath,
  readFeishuEntry,
  resolveFixedPortalLeaf,
  withFeishuEntry,
} from '../navigation/portalNavigation';
import {
  authorizedRuntimePrimaryNavigation,
  shouldRetainRuntimePrimaryNavigation,
} from '../navigation/runtimePrimaryNavigation';
import {
  invalidateAllRegisteredNavigationApplications,
  invalidateRegisteredNavigationApplication,
  selectRegisteredApplicationPrimaryNavigation,
} from '../runtime/wujieLoader';

function replacePath(path: string, feishuAppId?: string) {
  window.history.replaceState({}, '', withFeishuEntry(path, feishuAppId));
  window.dispatchEvent(new PopStateEvent('popstate'));
}

export function navigationApplicationFingerprint(application: PortalApplication) {
  return JSON.stringify([
    application.entry.embedUrl,
    application.entry.basePath,
    application.entry.defaultRoute,
  ]);
}

export function applicationsRequiringNavigationInvalidation(
  previous: PortalBootstrap | undefined,
  next: PortalBootstrap,
) {
  if (!previous) return [];
  const nextByCode = new Map(next.applications.map(application => [
    application.appCode,
    application,
  ]));
  return previous.applications.flatMap(application => {
    const current = nextByCode.get(application.appCode);
    if (!current
      || resolveApplicationRuntimeState(current.status) !== 'ready'
      || navigationApplicationFingerprint(current)
        !== navigationApplicationFingerprint(application)) {
      return [application.appCode];
    }
    return [];
  });
}

const ignorePrimaryNavigationChange = () => undefined;

type PrimaryNavigationLoadState = {
  appCode: string;
  mountId: string;
  status: 'loading' | 'ready' | 'empty' | 'failed';
};

type ApplicationSwitchAttempt = {
  attemptId: string;
  targetAppCode: string;
  targetMountId: string;
  targetPathname: string;
  restoringPathname: boolean;
  restoredNavigationCode?: string;
  source: {
    pathname: string;
    applicationCode?: string;
    navigationSnapshot: readonly RuntimePrimaryNavigationItem[];
    activePrimaryNavigation?: {
      appCode: string;
      navigationCode: string;
    };
  };
  deadlineAt: number;
  stage: 'access-token' | 'runtime-start' | 'navigation-register' | 'commit';
  runtimeReady: boolean;
  navigationReady: boolean;
  firstNavigationCode?: string;
};

export const APPLICATION_SWITCH_TIMEOUT_MS = 20_000;

type VisibleMountIdentity = {
  appCode: string;
  mountId: string;
  attemptId?: string;
  ready: boolean;
};

type RememberedApplicationLocation = {
  pathname: string;
  navigationCode?: string;
};

function createPrimaryNavigationLoadState(appCode: string): PrimaryNavigationLoadState {
  return {
    appCode,
    mountId: crypto.randomUUID(),
    status: 'loading',
  };
}

export function App() {
  const mobileViewport = useMobileViewport();
  const mobileLayoutRef = useRef(mobileViewport);
  const { message, reportOperationFeedback } = usePortalOverlay();
  const formSessionManagerRef = useRef<PortalFormSessionManager | undefined>(undefined);
  const taskSelectionsRef = useRef<TaskFormSelectionCoordinator | undefined>(undefined);
  const draftSelectionsRef = useRef<DraftFormSelectionCoordinator | undefined>(undefined);
  const navigationSelectionRef = useRef<{ target: string; selection?: PreparedTaskSelection; draftSelection?: PreparedDraftSelection } | undefined>(undefined);
  const [preparedTaskSelection, setPreparedTaskSelection] = useState<PreparedTaskSelection>();
  const [preparedDraftSelection, setPreparedDraftSelection] = useState<PreparedDraftSelection>();
  const [formNavigationLoading, setFormNavigationLoading] = useState<{ id: number; message: string }>();
  const [pathname, setPathname] = useState(() => normalizePortalPath(window.location.pathname));
  const pathnameRef = useRef(pathname);
  const [feishuEntry, setFeishuEntry] = useState(() => readFeishuEntry(window.location.search));
  const feishuEntryRef = useRef(feishuEntry);
  const entryGenerationRef = useRef(0);
  const feishuExchangeRef = useRef<AbortController | undefined>(undefined);
  const [feishuLoginPhase, setFeishuLoginPhase] = useState<'idle' | 'failed' | 'exhausted'>('idle');
  const feishuLoginPhaseRef = useRef<'idle' | 'failed' | 'exhausted'>('idle');
  const logoutSuppressedRef = useRef(false);
  const passwordLoginPendingRef = useRef(false);
  const recoveryRef = useRef<Promise<void> | undefined>(undefined);
  const [recoveryCycle, setRecoveryCycle] = useState(0);
  const [session, setSession] = useState<AuthSession>();
  const [bootstrap, setBootstrap] = useState<PortalBootstrap>();
  // Changing Shell type remounts page controllers and disconnects Wujie custom elements.
  // Hold the current layout while away from the workbench; apply the latest viewport only
  // after an allowed return, or before authenticated content exists. No leave is forced.
  if (!bootstrap || pathname === '/portal/workbench' || pathname === '/login') {
    mobileLayoutRef.current = mobileViewport;
  }
  const mobile = mobileLayoutRef.current;
  const bootstrapRef = useRef<PortalBootstrap | undefined>(undefined);
  const [checking, setChecking] = useState(true);
  const [loadingBootstrap, setLoadingBootstrap] = useState(false);
  const [bootstrapError, setBootstrapError] = useState<string>();
  const [selectedApplicationCode, setSelectedApplicationCode] = useState<string>();
  const [activePrimaryNavigation, setActivePrimaryNavigation] = useState<{
    appCode: string;
    navigationCode: string;
  }>();
  const [primaryNavigationByApplication, setPrimaryNavigationByApplication]
    = useState<Record<string, readonly RuntimePrimaryNavigationItem[]>>({});
  const primaryNavigationByApplicationRef = useRef<
    Record<string, readonly RuntimePrimaryNavigationItem[]>
  >({});
  const declaredPrimaryNavigationRef = useRef<Record<string, readonly RuntimePrimaryNavigationItem[]>>({});
  const [primaryNavigationLoad, setPrimaryNavigationLoad]
    = useState<PrimaryNavigationLoadState>();
  const primaryNavigationLoadRef = useRef<PrimaryNavigationLoadState | undefined>(undefined);
  const [applicationSwitchAttempt, setApplicationSwitchAttempt]
    = useState<ApplicationSwitchAttempt>();
  const applicationSwitchAttemptRef = useRef<ApplicationSwitchAttempt | undefined>(undefined);
  const visibleMountIdentityRef = useRef<VisibleMountIdentity | undefined>(undefined);
  const pendingPrimaryNavigation = useRef<{
    appCode: string;
    navigationCode: string;
  } | undefined>(undefined);
  const backgroundNavigationIssue = useRef<Record<
    string,
    'empty' | 'failed' | 'unavailable'
  >>({});
  const rememberedLocationByApplicationRef = useRef<
    Record<string, RememberedApplicationLocation>
  >({});

  const rememberApplicationPathname = useCallback((
    application: PortalApplication,
    nextPathname: string,
  ) => {
    const normalized = normalizePortalPath(nextPathname);
    if (!isApplicationPathname(application, normalized)) return;
    rememberedLocationByApplicationRef.current = {
      ...rememberedLocationByApplicationRef.current,
      [application.appCode]: {
        ...rememberedLocationByApplicationRef.current[application.appCode],
        pathname: normalized,
      },
    };
  }, []);

  const rememberApplicationNavigation = useCallback((
    application: PortalApplication,
    navigationCode: string,
  ) => {
    const remembered = rememberedLocationByApplicationRef.current[application.appCode];
    const rememberedPathname = remembered?.pathname ?? pathnameRef.current;
    if (!isApplicationPathname(application, rememberedPathname)) return;
    rememberedLocationByApplicationRef.current = {
      ...rememberedLocationByApplicationRef.current,
      [application.appCode]: {
        pathname: rememberedPathname,
        navigationCode,
      },
    };
  }, []);

  const rememberCurrentVisibleApplicationPathname = useCallback((
    application: PortalApplication,
    nextPathname: string,
  ) => {
    const visibleMount = visibleMountIdentityRef.current;
    if (!visibleMount?.ready || visibleMount.appCode !== application.appCode) return;
    rememberApplicationPathname(application, nextPathname);
  }, [rememberApplicationPathname]);

  const updatePrimaryNavigationSnapshot = useCallback((
    appCode: string,
    items: readonly RuntimePrimaryNavigationItem[],
  ) => {
    const next = { ...primaryNavigationByApplicationRef.current, [appCode]: items };
    primaryNavigationByApplicationRef.current = next;
    setPrimaryNavigationByApplication(next);
  }, []);

  const updateApplicationSwitchAttempt = useCallback((
    next: ApplicationSwitchAttempt | undefined,
  ) => {
    applicationSwitchAttemptRef.current = next;
    setApplicationSwitchAttempt(next);
  }, []);

  useEffect(() => subscribeApplicationPermissions(appCode => {
    const declared = declaredPrimaryNavigationRef.current[appCode];
    if (declared) updatePrimaryNavigationSnapshot(appCode,
      authorizedRuntimePrimaryNavigation(declared, applicationPermissionCodes(appCode)));
  }), [updatePrimaryNavigationSnapshot]);

  const navigationGuardRef = useRef<FormNavigationGuard | undefined>(undefined);
  if (!navigationGuardRef.current) navigationGuardRef.current = new FormNavigationGuard({
    manager: () => formSessionManagerRef.current,
    history: window.history,
    location: () => `${window.location.pathname}${window.location.search}`,
    commit: target => {
      const normalized = normalizePortalPath(target.split('?')[0]);
      pathnameRef.current = normalized;
      setPathname(normalized);
      const prepared = navigationSelectionRef.current;
      setPreparedTaskSelection(prepared?.target === target ? prepared.selection : undefined);
      const selection = prepared?.target === target ? prepared.selection : undefined;
      if (selection?.pendingForm) {
        void selection.pendingForm.then(completed => {
          if (!taskSelectionsRef.current?.isCurrent(completed)) return;
          setPreparedTaskSelection(current => current === selection ? completed : current);
        }, () => { /* Superseded selections never update the displayed task. */ });
      }
      setPreparedDraftSelection(prepared?.target === target ? prepared.draftSelection : undefined);
      navigationSelectionRef.current = undefined;
      setFormNavigationLoading(undefined);
    },
    onError: cause => message.error(errorMessage(cause)),
  });
  const navigationGuard = navigationGuardRef.current;

  const prepareNavigation = useCallback(async (target: string, transaction: FormNavigationTransaction,
    options: TaskNavigationOptions = {}) => {
    const path = normalizePortalPath(target.split('?')[0]);
    const taskRoute = path === '/portal/todos' || path.startsWith('/portal/todos/')
      ? parseMyTasksRoute(path) : undefined;
    const manager = formSessionManagerRef.current;
    if (taskRoute?.taskId && manager && bootstrapRef.current) {
      taskSelectionsRef.current ??= new TaskFormSelectionCoordinator(manager,
        () => bootstrapRef.current?.applications ?? []);
      const preparation = taskSelectionsRef.current.prepare(taskRoute.group, taskRoute.taskId, {
        force: options.force, isCurrent: transaction.isCurrent,
        leaveCurrent: () => navigationGuard.leave(transaction),
      });
      // A busy rejection happens synchronously before the manager accepts this intent;
      // it must not revoke the route owner of an already-running save-and-switch.
      if (!transaction.claim()) return false;
      setFormNavigationLoading({ id: transaction.id, message: '正在加载任务详情...' });
      const selection = await preparation;
      if (!transaction.isCurrent() || !taskSelectionsRef.current.isCurrent(selection)) return false;
      navigationSelectionRef.current = { target, selection };
      return true; // The old form has been left; source readiness may still be pending.
    }
    const draftId = path.startsWith('/portal/drafts/') ? path.slice('/portal/drafts/'.length) : undefined;
    if (draftId && manager && bootstrapRef.current) {
      draftSelectionsRef.current ??= new DraftFormSelectionCoordinator(manager,
        () => bootstrapRef.current?.applications ?? [], () => bootstrapRef.current!.identity);
      const preparation = draftSelectionsRef.current.prepare(draftId, {
        force: options.force, isCurrent: transaction.isCurrent,
        leaveCurrent: () => navigationGuard.leave(transaction),
      });
      if (!transaction.claim()) return false;
      setFormNavigationLoading({ id: transaction.id, message: '正在加载草稿...' });
      const draftSelection = await preparation;
      if (!transaction.isCurrent() || !draftSelectionsRef.current.isCurrent(draftSelection)) return false;
      navigationSelectionRef.current = { target, draftSelection };
      return true;
    }
    if (!await navigationGuard.leave(transaction)) return false;
    navigationSelectionRef.current = { target };
    return true;

  }, [navigationGuard]);

  const navigate = useCallback((path: string, options: TaskNavigationOptions = {}): Promise<boolean> => {
    const target = withFeishuEntry(normalizePortalPath(path), feishuEntryRef.current.appId);
    let transactionId: number | undefined;
    return navigationGuard.navigate(target, transaction => {
      transactionId = transaction.id;
      return prepareNavigation(target, transaction, options);
    }, options.replace).finally(() => {
      setFormNavigationLoading(current => current?.id === transactionId ? undefined : current);
    });
  }, [navigationGuard, prepareNavigation]);

  const clearSessionState = useCallback(() => {
    formSessionManagerRef.current?.setAuthenticatedContext(undefined);
    void invalidateAllRegisteredNavigationApplications().catch(() => undefined);
    clearAccessTokens();
    declaredPrimaryNavigationRef.current = {};
    setSession(undefined);
    bootstrapRef.current = undefined;
    setBootstrap(undefined);
    setSelectedApplicationCode(undefined);
    primaryNavigationByApplicationRef.current = {};
    setPrimaryNavigationByApplication({});
    primaryNavigationLoadRef.current = undefined;
    setPrimaryNavigationLoad(undefined);
    setActivePrimaryNavigation(undefined);
    pendingPrimaryNavigation.current = undefined;
    applicationSwitchAttemptRef.current = undefined;
    setApplicationSwitchAttempt(undefined);
    visibleMountIdentityRef.current = undefined;
    backgroundNavigationIssue.current = {};
    rememberedLocationByApplicationRef.current = {};
    taskSelectionsRef.current = undefined;
    draftSelectionsRef.current = undefined;
    navigationSelectionRef.current = undefined;
    setPreparedTaskSelection(undefined);
    setPreparedDraftSelection(undefined);
    setFormNavigationLoading(undefined);
  }, []);

  const recoverSession = useCallback(() => {
    if (!bootstrapRef.current || recoveryRef.current) return;
    const generation = entryGenerationRef.current;
    setChecking(true);
    const recovery = authApi.current(feishuEntryRef.current.appId)
      .then(() => {
        // A business request can fail independently while the platform Session remains valid.
        if (generation === entryGenerationRef.current) setChecking(false);
      })
      .catch(error => {
        if (generation !== entryGenerationRef.current) return;
        if (error instanceof ApiError && error.status === 401) {
          entryGenerationRef.current += 1;
          feishuExchangeRef.current?.abort();
          feishuExchangeRef.current = undefined;
          clearSessionState();
          setRecoveryCycle(cycle => cycle + 1);
        } else {
          setChecking(false);
          setBootstrapError(errorMessage(error));
        }
      })
      .finally(() => {
        if (recoveryRef.current === recovery) recoveryRef.current = undefined;
      });
    recoveryRef.current = recovery;
  }, [clearSessionState]);

  const handlePasswordChanged = useCallback(() => {
    entryGenerationRef.current += 1;
    feishuExchangeRef.current?.abort();
    feishuExchangeRef.current = undefined;
    clearSessionState();
    setChecking(false);
    replacePath('/login');
  }, [clearSessionState]);

  useEffect(() => subscribePlatformSessionInvalidation(recoverSession), [recoverSession]);

  useEffect(() => () => {
    entryGenerationRef.current += 1;
    feishuExchangeRef.current?.abort();
    feishuExchangeRef.current = undefined;
  }, []);

  const loadBootstrap = useCallback(async () => {
    const generation = entryGenerationRef.current;
    setLoadingBootstrap(true);
    setBootstrapError(undefined);
    try {
      formSessionManagerRef.current?.setAuthenticatedContext(undefined);
      await invalidateAllRegisteredNavigationApplications();
      if (generation !== entryGenerationRef.current) return;
      clearAccessTokens();
      declaredPrimaryNavigationRef.current = {};
      const nextBootstrap = await portalApi.bootstrap();
      if (generation !== entryGenerationRef.current) return;
      const applicationCodes = applicationsRequiringNavigationInvalidation(
        bootstrapRef.current,
        nextBootstrap,
      );
      await Promise.all(applicationCodes.map(applicationCode =>
        invalidateRegisteredNavigationApplication(applicationCode)));
      if (generation !== entryGenerationRef.current) return;
      bootstrapRef.current = nextBootstrap;
      setBootstrap(nextBootstrap);
      primaryNavigationByApplicationRef.current = {};
      setPrimaryNavigationByApplication({});
      primaryNavigationLoadRef.current = undefined;
      setPrimaryNavigationLoad(undefined);
      applicationSwitchAttemptRef.current = undefined;
      setApplicationSwitchAttempt(undefined);
      visibleMountIdentityRef.current = undefined;
      backgroundNavigationIssue.current = {};
      rememberedLocationByApplicationRef.current = {};
    } catch (error) {
      if (generation !== entryGenerationRef.current) return;
      if (error instanceof ApiError && error.status === 401) {
        entryGenerationRef.current += 1;
        clearSessionState();
        setChecking(true);
        setRecoveryCycle(cycle => cycle + 1);
      } else setBootstrapError(errorMessage(error));
    } finally {
      if (generation === entryGenerationRef.current) setLoadingBootstrap(false);
    }
  }, [clearSessionState]);

  useEffect(() => {
    const onPopState = (event: PopStateEvent) => {
      const next = readFeishuEntry(window.location.search);
      const previous = feishuEntryRef.current;
      if (next.appId === previous.appId && next.invalid === previous.invalid) {
        if (!bootstrapRef.current) { navigationGuard.adoptForcedLocation(); return; }
        const target = `${window.location.pathname}${window.location.search}`;
        let transactionId: number | undefined;
        void navigationGuard.handlePopState(event.state, transaction => {
          transactionId = transaction.id;
          return prepareNavigation(target, transaction);
        }).finally(() => {
          setFormNavigationLoading(current => current?.id === transactionId ? undefined : current);
        });
        return;
      }
      entryGenerationRef.current += 1;
      feishuExchangeRef.current?.abort();
      feishuExchangeRef.current = undefined;
      feishuLoginPhaseRef.current = 'idle';
      setFeishuLoginPhase('idle');
      feishuEntryRef.current = next;
      clearSessionState();
      setChecking(true);
      setFeishuEntry(next);
      navigationGuard.adoptForcedLocation();
    };
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, [clearSessionState, navigationGuard, prepareNavigation]);

  const finishAuthenticatedSession = useCallback(async (
    current: AuthSession, generation: number, appId?: string,
  ) => {
    await invalidateAllRegisteredNavigationApplications();
    if (generation !== entryGenerationRef.current) return;
    const currentBootstrap = await portalApi.bootstrap();
    if (generation !== entryGenerationRef.current) return;
    bootstrapRef.current = currentBootstrap;
    setBootstrap(currentBootstrap);
    setSession(current);
    setBootstrapError(undefined);
    if (feishuLoginPhaseRef.current !== 'exhausted') {
      feishuLoginPhaseRef.current = 'idle';
      setFeishuLoginPhase('idle');
    }
    if (window.location.pathname === '/login') replacePath(defaultPortalPath, appId);
  }, []);

  const attemptFeishuLogin = useCallback(async (
    appId: string, generation: number, manual: boolean,
  ) => {
    if (feishuExchangeRef.current || generation !== entryGenerationRef.current
        || logoutSuppressedRef.current) return;
    const controller = new AbortController();
    feishuExchangeRef.current = controller;
    setChecking(true);
    setBootstrapError(undefined);
    if (manual) setFeishuLoginPhase('idle');
    let attemptGeneration = generation;
    const isCurrent = () => attemptGeneration === entryGenerationRef.current
      && feishuExchangeRef.current === controller;
    const onTimeout = () => {
      if (!isCurrent()) return;
      attemptGeneration = ++entryGenerationRef.current;
    };
    try {
      const code = await withAbortTimeout(
        acquireFeishuCode(appId, controller.signal), controller, undefined, onTimeout,
      );
      if (!isCurrent()) return;
      await withAbortTimeout(
        authApi.feishuSession(appId, code, controller.signal), controller, undefined, onTimeout,
      );
      if (!isCurrent()) return;
      const current = await authApi.current(appId);
      if (!isCurrent()) return;
      await finishAuthenticatedSession(current, attemptGeneration, appId);
      if (isCurrent()) {
        feishuLoginPhaseRef.current = 'idle';
        setFeishuLoginPhase('idle');
      }
    } catch {
      if (!isCurrent()) return;
      clearSessionState();
      const nextPhase = manual ? 'exhausted' : 'failed';
      feishuLoginPhaseRef.current = nextPhase;
      setFeishuLoginPhase(nextPhase);
      replacePath('/login', appId);
    } finally {
      if (feishuExchangeRef.current === controller) {
        feishuExchangeRef.current = undefined;
        if (attemptGeneration === entryGenerationRef.current) setChecking(false);
      }
    }
  }, [clearSessionState, finishAuthenticatedSession]);

  useEffect(() => {
    let active = true;
    const generation = entryGenerationRef.current;
    const isCurrent = () => active && generation === entryGenerationRef.current;
    if (window.location.pathname === '/login'
        && (hasFeishuWebCallback(window.location.search)
          || hasFeishuWebStartHandoff(window.location.search))) {
      setChecking(false);
      return () => { active = false; };
    }
    if (feishuEntry.invalid) {
      window.history.replaceState({}, '', '/login');
      setPathname('/login');
      setChecking(false);
      return () => { active = false; };
    }
    const manualPasswordLogin = passwordLoginPendingRef.current;
    passwordLoginPendingRef.current = false;
    if (!manualPasswordLogin && (logoutSuppressedRef.current
        || feishuLoginPhaseRef.current === 'exhausted')) {
      setChecking(false);
      return () => { active = false; };
    }
    setChecking(true);
    void (async () => {
      try {
        const current = await authApi.current(feishuEntry.appId);
        if (!isCurrent()) return;
        await finishAuthenticatedSession(current, generation, feishuEntry.appId);
      } catch (error) {
        if (!isCurrent()) return;
        clearSessionState();
        if (error instanceof ApiError && error.status === 401
            && canLoadFeishuSdk(feishuEntry.appId)
            && feishuLoginPhaseRef.current === 'idle') {
          await attemptFeishuLogin(feishuEntry.appId!, generation, false);
          return;
        }
        if (!(error instanceof ApiError && error.status === 401)) {
          setBootstrapError(errorMessage(error));
        }
        replacePath('/login', feishuEntry.appId);
      } finally {
        if (isCurrent() && !feishuExchangeRef.current) setChecking(false);
      }
    })();
    return () => { active = false; };
  }, [attemptFeishuLogin, clearSessionState, feishuEntry,
    finishAuthenticatedSession, recoveryCycle]);

  const retryFeishuLogin = useCallback(() => {
    if (feishuLoginPhaseRef.current !== 'failed' || feishuExchangeRef.current
        || !feishuEntryRef.current.appId || logoutSuppressedRef.current) return;
    feishuLoginPhaseRef.current = 'exhausted';
    const generation = ++entryGenerationRef.current;
    void attemptFeishuLogin(feishuEntryRef.current.appId,
      generation, true);
  }, [attemptFeishuLogin]);

  const logout = useCallback(async () => {
    if (feishuExchangeRef.current || logoutSuppressedRef.current) return;
    if (!await navigate('/login')) return;
    logoutSuppressedRef.current = true;
    entryGenerationRef.current += 1;
    clearSessionState();
    setChecking(false);
    setBootstrapError(undefined);
    replacePath('/login', feishuEntryRef.current.appId);
    void authApi.logout().catch(error => {
      // The current page remains suppressed even if revocation fails.
      setBootstrapError(errorMessage(error));
    });
  }, [clearSessionState, navigate]);

  const applications = useMemo<PortalApplication[]>(() => {
    return bootstrap?.applications ?? [];
  }, [bootstrap]);

  const routedApplication = useMemo(
    () => findRoutedApplication(applications, pathname),
    [applications, pathname],
  );
  const currentApplication = useMemo(
    () => resolveCurrentApplication(applications, pathname, selectedApplicationCode),
    [applications, pathname, selectedApplicationCode],
  );
  const currentApplicationCodeRef = useRef<string | undefined>(undefined);
  pathnameRef.current = pathname;
  currentApplicationCodeRef.current = currentApplication?.appCode;
  const isCurrentVisibleMount = useCallback((mountId: string | undefined) => {
    const current = visibleMountIdentityRef.current;
    return Boolean(mountId && current?.mountId === mountId
      && current.appCode === routedApplication?.appCode);
  }, [routedApplication?.appCode]);
  const handlePrimaryNavigationChange = useCallback((
    navigationCode: string,
    mountId?: string,
  ) => {
    if (!routedApplication || !isCurrentVisibleMount(mountId)) return;
    rememberApplicationNavigation(routedApplication, navigationCode);
    setActivePrimaryNavigation({
      appCode: routedApplication.appCode,
      navigationCode,
    });
  }, [isCurrentVisibleMount, rememberApplicationNavigation, routedApplication]);
  const registerPrimaryNavigation = useCallback((
    appCode: string,
    items: readonly RuntimePrimaryNavigationItem[],
    runtimeRegistration = false,
  ) => {
    if (!runtimeRegistration && shouldRetainRuntimePrimaryNavigation(
      appCode,
      currentApplicationCodeRef.current,
      pathnameRef.current,
      items,
      Boolean(primaryNavigationByApplicationRef.current[appCode]?.length),
    )) return undefined;
    const application = applications.find(candidate => candidate.appCode === appCode);
    if (!application || !bootstrap) return undefined;
    declaredPrimaryNavigationRef.current[appCode] = items;
    const authorized = authorizedRuntimePrimaryNavigation(
      items,
      applicationPermissionCodes(appCode),
    );
    updatePrimaryNavigationSnapshot(appCode, authorized);
    const pending = pendingPrimaryNavigation.current;
    if (pending?.appCode === appCode
      && authorized.some(item => item.code === pending.navigationCode)) {
      pendingPrimaryNavigation.current = undefined;
      queueMicrotask(() => selectRegisteredApplicationPrimaryNavigation(
        application,
        pending.navigationCode,
      ));
    }
    return authorized;
  }, [applications, bootstrap, updatePrimaryNavigationSnapshot]);
  const handleCurrentPrimaryNavigationRegister = useCallback((
    items: readonly RuntimePrimaryNavigationItem[],
    runtimeRegistration?: boolean,
  ) => {
    if (routedApplication) registerPrimaryNavigation(
      routedApplication.appCode,
      items,
      runtimeRegistration,
    );
  }, [registerPrimaryNavigation, routedApplication]);

  const handleCurrentLoadFailed = useCallback(() => {
    if (!routedApplication) return;
    updatePrimaryNavigationSnapshot(routedApplication.appCode, []);
    if (pendingPrimaryNavigation.current?.appCode === routedApplication.appCode) {
      pendingPrimaryNavigation.current = undefined;
    }
  }, [routedApplication, updatePrimaryNavigationSnapshot]);

  const updatePrimaryNavigationLoad = useCallback((
    next: PrimaryNavigationLoadState | undefined,
  ) => {
    primaryNavigationLoadRef.current = next;
    setPrimaryNavigationLoad(next);
  }, []);

  const isCurrentPrimaryNavigationLoad = useCallback((
    appCode: string,
    mountId?: string,
  ) => {
    const current = primaryNavigationLoadRef.current;
    return Boolean(mountId
      && current?.appCode === appCode
      && current.mountId === mountId);
  }, []);

  const reportBackgroundNavigationIssue = useCallback((
    application: PortalApplication,
    issue: 'empty' | 'failed' | 'unavailable',
  ) => {
    if (backgroundNavigationIssue.current[application.appCode] === issue) return;
    backgroundNavigationIssue.current[application.appCode] = issue;
    if (issue === 'failed') {
      message.error(`${application.appName}导航加载失败，已保持在工作台`);
    } else if (issue === 'unavailable') {
      message.warning(`${application.appName}当前不可用，已保持在工作台`);
    } else {
      message.warning(`${application.appName}没有可用的一级导航权限，已保持在工作台`);
    }
  }, [message]);

  const handleBackgroundPrimaryNavigationRegister = useCallback((
    application: PortalApplication,
    items: readonly RuntimePrimaryNavigationItem[],
    runtimeRegistration?: boolean,
    mountId?: string,
  ) => {
    if (runtimeRegistration
      && !isCurrentPrimaryNavigationLoad(application.appCode, mountId)) return;
    const authorized = registerPrimaryNavigation(
      application.appCode,
      items,
      runtimeRegistration,
    );
    if (!runtimeRegistration || !authorized) return;
    // React StrictMode 会在开发态重放一次 Effect 清理。子应用在清理时注册空数组
    // 只表示当前导航暂时撤销，不能据此判定用户没有导航权限；继续等待重注册
    // 或由既有加载截止时间收口。非空声明经过权限过滤后为空仍是真实无权限。
    if (Array.isArray(items) && items.length === 0) return;
    if (!authorized.length) {
      updatePrimaryNavigationLoad({
        appCode: application.appCode,
        mountId: mountId!,
        status: 'empty',
      });
      reportBackgroundNavigationIssue(application, 'empty');
      return;
    }
    updatePrimaryNavigationLoad({
      appCode: application.appCode,
      mountId: mountId!,
      status: 'ready',
    });
    delete backgroundNavigationIssue.current[application.appCode];
  }, [isCurrentPrimaryNavigationLoad, registerPrimaryNavigation,
    reportBackgroundNavigationIssue, updatePrimaryNavigationLoad]);

  const handleBackgroundLoadFailed = useCallback((
    application: PortalApplication,
    mountId: string,
  ) => {
    if (!isCurrentPrimaryNavigationLoad(application.appCode, mountId)) return;
    updatePrimaryNavigationLoad({
      appCode: application.appCode,
      mountId,
      status: 'failed',
    });
    updatePrimaryNavigationSnapshot(application.appCode, []);
    reportBackgroundNavigationIssue(application, 'failed');
  }, [isCurrentPrimaryNavigationLoad, reportBackgroundNavigationIssue,
    updatePrimaryNavigationLoad, updatePrimaryNavigationSnapshot]);

  const failApplicationSwitch = useCallback((
    application: PortalApplication,
    issue: 'empty' | 'failed' | 'timeout' | 'unavailable',
    mountId?: string,
    attemptId?: string,
  ) => {
    const current = applicationSwitchAttemptRef.current;
    if (current && (current.targetAppCode !== application.appCode
      || (mountId && current.targetMountId !== mountId)
      || (attemptId && current.attemptId !== attemptId))) return;
    if (current) {
      applicationSwitchAttemptRef.current = undefined;
      setApplicationSwitchAttempt(undefined);
      const nextSnapshots = {
        ...primaryNavigationByApplicationRef.current,
        [current.targetAppCode]: [],
      };
      if (current.source.applicationCode) {
        nextSnapshots[current.source.applicationCode] = current.source.navigationSnapshot;
      }
      primaryNavigationByApplicationRef.current = nextSnapshots;
      setPrimaryNavigationByApplication(nextSnapshots);
      visibleMountIdentityRef.current = undefined;
      pendingPrimaryNavigation.current = undefined;
      setSelectedApplicationCode(current.source.applicationCode);
      setActivePrimaryNavigation(current.source.activePrimaryNavigation);
      replacePath(current.source.pathname, feishuEntryRef.current.appId);
      void invalidateRegisteredNavigationApplication(current.targetAppCode)
        .catch(() => undefined);
      console.warn('应用切换未完成', {
        stage: current.stage,
        outcome: issue === 'unavailable' ? 'unavailable'
          : issue === 'empty' ? 'navigation-empty' : issue,
      });
    }
    updatePrimaryNavigationSnapshot(application.appCode, []);
    if (issue === 'timeout') {
      message.error(`${application.appName}切换超时，请稍后重试`);
    } else if (issue === 'failed') {
      message.error(`${application.appName}加载失败，已恢复切换前页面`);
    } else if (issue === 'unavailable') {
      message.warning(`${application.appName}当前不可用，已保持当前页面`);
    } else {
      message.warning(`${application.appName}没有可用的一级导航权限，已恢复切换前页面`);
    }
  }, [message, updatePrimaryNavigationSnapshot]);

  const advanceApplicationSwitch = useCallback((
    attemptId: string,
    mountId: string,
    ready: Partial<Pick<ApplicationSwitchAttempt,
      'runtimeReady' | 'navigationReady' | 'firstNavigationCode'>>,
  ) => {
    const current = applicationSwitchAttemptRef.current;
    if (!current || current.attemptId !== attemptId
      || current.targetMountId !== mountId) return;
    const next = { ...current, ...ready };
    if (!next.runtimeReady || !next.navigationReady || !next.firstNavigationCode) {
      updateApplicationSwitchAttempt(next);
      return;
    }
    const application = applications.find(candidate => candidate.appCode === next.targetAppCode);
    if (!application || resolveApplicationRuntimeState(application.status) !== 'ready') {
      if (application) failApplicationSwitch(application, 'unavailable', mountId);
      else updateApplicationSwitchAttempt(undefined);
      return;
    }
    const committing = { ...next, stage: 'commit' as const };
    applicationSwitchAttemptRef.current = committing;
    setActivePrimaryNavigation(next.restoringPathname
      ? next.restoredNavigationCode
        ? {
          appCode: application.appCode,
          navigationCode: next.restoredNavigationCode,
        }
        : undefined
      : {
        appCode: application.appCode,
        navigationCode: next.firstNavigationCode,
      });
    pendingPrimaryNavigation.current = undefined;
    rememberApplicationPathname(application, next.targetPathname);
    if (!next.restoringPathname) {
      selectRegisteredApplicationPrimaryNavigation(
        application,
        next.firstNavigationCode!,
      );
    }
    updateApplicationSwitchAttempt(undefined);
  }, [applications, failApplicationSwitch, rememberApplicationPathname,
    updateApplicationSwitchAttempt]);

  const handleSwitchPrimaryNavigationRegister = useCallback((
    items: readonly RuntimePrimaryNavigationItem[],
    runtimeRegistration?: boolean,
    mountId?: string,
    attemptId?: string,
  ) => {
    const current = applicationSwitchAttemptRef.current;
    if (!current || !runtimeRegistration || current.attemptId !== attemptId
      || current.targetMountId !== mountId) return;
    const application = applications.find(candidate => candidate.appCode === current.targetAppCode);
    if (!application) return;
    let authorized: readonly RuntimePrimaryNavigationItem[] | undefined;
    try {
      authorized = registerPrimaryNavigation(application.appCode, items, true);
    } catch {
      failApplicationSwitch(application, 'failed', mountId, attemptId);
      return;
    }
    // 空注册表示当前导航已撤销，等待重注册或原有截止时间收口。
    if (Array.isArray(items) && items.length === 0) {
      advanceApplicationSwitch(current.attemptId, current.targetMountId, {
        navigationReady: false,
        firstNavigationCode: undefined,
      });
      return;
    }
    if (!authorized?.length) {
      failApplicationSwitch(application, 'empty', mountId, attemptId);
      return;
    }
    advanceApplicationSwitch(current.attemptId, current.targetMountId, {
      navigationReady: true,
      firstNavigationCode: authorized[0].code,
    });
  }, [advanceApplicationSwitch, applications, failApplicationSwitch,
    registerPrimaryNavigation]);

  const handleSwitchLoadReady = useCallback((mountId: string, attemptId?: string) => {
    const current = applicationSwitchAttemptRef.current;
    if (!current || current.attemptId !== attemptId) return;
    advanceApplicationSwitch(attemptId, mountId, { runtimeReady: true });
  }, [advanceApplicationSwitch]);

  const handleSwitchLoadFailed = useCallback((mountId: string, attemptId?: string) => {
    const current = applicationSwitchAttemptRef.current;
    if (!current || current.attemptId !== attemptId || current.targetMountId !== mountId) return;
    const application = applications.find(candidate => candidate.appCode === current.targetAppCode);
    if (application) failApplicationSwitch(application, 'failed', mountId, attemptId);
  }, [applications, failApplicationSwitch]);

  const handleVisiblePrimaryNavigationRegister = useCallback((
    items: readonly RuntimePrimaryNavigationItem[],
    runtimeRegistration?: boolean,
    mountId?: string,
    attemptId?: string,
  ) => {
    if (!isCurrentVisibleMount(mountId)) return;
    if (applicationSwitchAttemptRef.current) {
      handleSwitchPrimaryNavigationRegister(
        items,
        runtimeRegistration,
        mountId,
        attemptId,
      );
      return;
    }
    handleCurrentPrimaryNavigationRegister(items, runtimeRegistration);
  }, [handleCurrentPrimaryNavigationRegister, handleSwitchPrimaryNavigationRegister,
    isCurrentVisibleMount]);

  const handleVisibleLoadReady = useCallback((mountId: string, attemptId?: string) => {
    if (!isCurrentVisibleMount(mountId)) return;
    const visibleMount = visibleMountIdentityRef.current;
    if (visibleMount?.mountId === mountId) {
      visibleMountIdentityRef.current = { ...visibleMount, ready: true };
    }
    const current = applicationSwitchAttemptRef.current;
    if (current) {
      handleSwitchLoadReady(mountId, attemptId);
      return;
    }
    if (routedApplication) rememberApplicationPathname(routedApplication, pathname);
  }, [handleSwitchLoadReady, isCurrentVisibleMount, pathname,
    rememberApplicationPathname, routedApplication]);

  const handleVisibleLoadFailed = useCallback((mountId: string, attemptId?: string) => {
    if (!isCurrentVisibleMount(mountId)) return;
    const visibleMount = visibleMountIdentityRef.current;
    if (visibleMount?.mountId === mountId) {
      visibleMountIdentityRef.current = { ...visibleMount, ready: false };
    }
    const current = applicationSwitchAttemptRef.current;
    if (!current) {
      handleCurrentLoadFailed();
      return;
    }
    if (current.attemptId !== attemptId || current.targetMountId !== mountId) return;
    const application = applications.find(candidate => candidate.appCode === current.targetAppCode);
    if (application) failApplicationSwitch(application, 'failed', mountId, attemptId);
  }, [applications, failApplicationSwitch, handleCurrentLoadFailed,
    isCurrentVisibleMount]);

  const handleVisibleLoadStageChange = useCallback((
    stage: ApplicationSwitchAttempt['stage'],
    mountId: string,
    attemptId?: string,
  ) => {
    if (stage === 'commit') return;
    const current = applicationSwitchAttemptRef.current;
    if (!current || current.attemptId !== attemptId
      || current.targetMountId !== mountId || current.stage === stage) return;
    updateApplicationSwitchAttempt({ ...current, stage });
  }, [updateApplicationSwitchAttempt]);

  const navigateFromVisibleMicroApp = useCallback((path: string, mountId?: string) => {
    if (!isCurrentVisibleMount(mountId)) return;
    const current = applicationSwitchAttemptRef.current;
    if (!current) {
      if (!routedApplication || !isApplicationPathname(routedApplication, path)) return;
      rememberApplicationPathname(routedApplication, path);
      navigate(path);
      return;
    }
    const application = applications.find(candidate => candidate.appCode === current.targetAppCode);
    if (visibleMountIdentityRef.current?.attemptId === current.attemptId
      && application && isApplicationPathname(application, path)) {
      const normalized = normalizePortalPath(path);
      updateApplicationSwitchAttempt({ ...current, targetPathname: normalized });
      navigate(normalized);
    }
  }, [applications, isCurrentVisibleMount, navigate, rememberApplicationPathname,
    routedApplication, updateApplicationSwitchAttempt]);

  const primaryNavigation = currentApplication
    ? primaryNavigationByApplication[currentApplication.appCode] ?? []
    : [];
  const backgroundApplication = isFixedPortalPath(pathname)
    && !routedApplication
    && currentApplication
    && !primaryNavigation.length
    && resolveApplicationRuntimeState(currentApplication.status) === 'ready'
    ? currentApplication : undefined;
  useLayoutEffect(() => {
    if (!backgroundApplication) {
      if (primaryNavigationLoadRef.current) updatePrimaryNavigationLoad(undefined);
      return;
    }
    if (primaryNavigationLoadRef.current?.appCode === backgroundApplication.appCode) return;
    updatePrimaryNavigationLoad(createPrimaryNavigationLoadState(
      backgroundApplication.appCode,
    ));
  }, [backgroundApplication, updatePrimaryNavigationLoad]);
  const backgroundMount = backgroundApplication
    && !applicationSwitchAttempt
    && primaryNavigationLoad?.appCode === backgroundApplication.appCode
    && primaryNavigationLoad.status === 'loading'
    ? primaryNavigationLoad : undefined;
  const handleCurrentBackgroundPrimaryNavigationRegister = useCallback((
    items: readonly RuntimePrimaryNavigationItem[],
    runtimeRegistration?: boolean,
    mountId?: string,
  ) => {
    if (backgroundApplication) {
      handleBackgroundPrimaryNavigationRegister(
        backgroundApplication,
        items,
        runtimeRegistration,
        mountId,
      );
    }
  }, [backgroundApplication, handleBackgroundPrimaryNavigationRegister]);
  const handleCurrentBackgroundLoadFailed = useCallback((mountId: string) => {
    if (backgroundApplication) handleBackgroundLoadFailed(backgroundApplication, mountId);
  }, [backgroundApplication, handleBackgroundLoadFailed]);

  useEffect(() => {
    const nextApplicationCode = routedApplication?.appCode
      ?? applications.find(application => application.appCode === selectedApplicationCode)?.appCode
      ?? findFirstEnabledApplication(applications)?.appCode;
    if (nextApplicationCode !== selectedApplicationCode) {
      setSelectedApplicationCode(nextApplicationCode);
    }
  }, [applications, routedApplication, selectedApplicationCode]);

  const selectApplication = useCallback(async (application: PortalApplication) => {
    if (application.appCode === currentApplication?.appCode
      || application.appCode === applicationSwitchAttemptRef.current?.targetAppCode) return;
    if (routedApplication) {
      rememberCurrentVisibleApplicationPathname(routedApplication, pathname);
    }
    delete backgroundNavigationIssue.current[application.appCode];
    if (resolveApplicationRuntimeState(application.status) !== 'ready') {
      failApplicationSwitch(application, 'unavailable');
      return;
    }
    const remembered = rememberedLocationByApplicationRef.current[application.appCode];
    const target = resolveApplicationSwitchPathname(application, remembered?.pathname);
    if (!await navigate(target.pathname)) return;
    const attempt: ApplicationSwitchAttempt = {
      attemptId: crypto.randomUUID(),
      targetAppCode: application.appCode,
      targetMountId: crypto.randomUUID(),
      targetPathname: target.pathname,
      restoringPathname: target.restored,
      restoredNavigationCode: target.restored ? remembered?.navigationCode : undefined,
      source: {
        pathname,
        applicationCode: currentApplication?.appCode,
        navigationSnapshot: currentApplication
          ? [...(primaryNavigationByApplicationRef.current[currentApplication.appCode] ?? [])]
          : [],
        activePrimaryNavigation: activePrimaryNavigation
          && activePrimaryNavigation.appCode === currentApplication?.appCode
          ? {
            appCode: activePrimaryNavigation.appCode,
            navigationCode: activePrimaryNavigation.navigationCode,
          }
          : undefined,
      },
      deadlineAt: Date.now() + APPLICATION_SWITCH_TIMEOUT_MS,
      stage: 'access-token',
      runtimeReady: false,
      navigationReady: false,
    };
    updateApplicationSwitchAttempt(attempt);
    visibleMountIdentityRef.current = {
      appCode: application.appCode,
      mountId: attempt.targetMountId,
      attemptId: attempt.attemptId,
      ready: false,
    };
    setSelectedApplicationCode(application.appCode);
    setActivePrimaryNavigation(undefined);
  }, [activePrimaryNavigation, currentApplication, failApplicationSwitch, pathname,
    navigate, rememberCurrentVisibleApplicationPathname, routedApplication,
    updateApplicationSwitchAttempt]);

  useEffect(() => {
    if (!applicationSwitchAttempt) return undefined;
    const timeoutId = window.setTimeout(() => {
      const current = applicationSwitchAttemptRef.current;
      if (!current || current.attemptId !== applicationSwitchAttempt.attemptId) return;
      const application = applications.find(candidate =>
        candidate.appCode === current.targetAppCode);
      if (!application) {
        updateApplicationSwitchAttempt(undefined);
        return;
      }
      failApplicationSwitch(
        application,
        'timeout',
        current.targetMountId,
        current.attemptId,
      );
    }, Math.max(0, applicationSwitchAttempt.deadlineAt - Date.now()));
    return () => window.clearTimeout(timeoutId);
  }, [applicationSwitchAttempt, applications, failApplicationSwitch,
    updateApplicationSwitchAttempt]);
  const navigateFromPortalShell = useCallback((nextPathname: string) => {
    if (routedApplication) {
      rememberCurrentVisibleApplicationPathname(routedApplication, pathname);
    }
    navigate(nextPathname);
  }, [navigate, pathname, rememberCurrentVisibleApplicationPathname, routedApplication]);
  const selectPrimaryNavigation = useCallback(async (navigationCode: string) => {
    if (!currentApplication || applicationSwitchAttemptRef.current) return;
    if (routedApplication?.appCode === currentApplication.appCode) {
      selectRegisteredApplicationPrimaryNavigation(
        currentApplication,
        navigationCode,
      );
      return;
    }
    if (!await navigate(`${currentApplication.entry.basePath}${currentApplication.entry.defaultRoute}`)) return;
    pendingPrimaryNavigation.current = {
      appCode: currentApplication.appCode,
      navigationCode,
    };
  }, [currentApplication, navigate, routedApplication]);

  const switchingApplication = applicationSwitchAttempt
    ? applications.find(application =>
      application.appCode === applicationSwitchAttempt.targetAppCode)
    : undefined;
  const formSessionManager = useAuthenticatedFormSessionManager({
    identity: session ? bootstrap?.identity : undefined,
    applications, navigate, onSessionExpired: recoverSession, reportOperationFeedback,
  });
  formSessionManagerRef.current = formSessionManager;
  const canShowFormNavigationLoading = useSyncExternalStore(formSessionManager.subscribe, () => {
    const { activeSession, latestRequest, openingSelection } = formSessionManager.getSnapshot();
    return activeSession?.status !== 'READY'
      || !!openingSelection && openingSelection === latestRequest;
  });
  const navigationLoadingMessage = canShowFormNavigationLoading ? formNavigationLoading?.message : undefined;
  if (checking) return <div className="platform-portal-boot"><Spin loading tip="正在恢复登录状态..." /></div>;
  if (!session || pathname === '/login') return <LoginPage
    autoLoginFailed={feishuLoginPhase === 'failed'}
    onRetryFeishu={retryFeishuLogin}
    externalError={bootstrapError}
    onLogin={current => {
    void (async () => {
      const generation = entryGenerationRef.current;
      if (feishuEntryRef.current.invalid) {
        feishuEntryRef.current = { invalid: false };
        window.history.replaceState({}, '', '/login');
      }
      if (feishuEntryRef.current.appId) {
        try {
          await authApi.current(feishuEntryRef.current.appId);
        } catch (error) {
          if (!(error instanceof ApiError && error.status === 401)) throw error;
          if (generation !== entryGenerationRef.current) return;
          entryGenerationRef.current += 1;
          feishuExchangeRef.current?.abort();
          feishuExchangeRef.current = undefined;
          clearSessionState();
          feishuEntryRef.current = { invalid: false };
          passwordLoginPendingRef.current = true;
          setFeishuEntry({ invalid: false });
          window.history.replaceState({}, '', '/login');
          setChecking(true);
          return;
        }
      }
      if (generation !== entryGenerationRef.current) return;
      if (feishuLoginPhaseRef.current !== 'exhausted') {
        feishuLoginPhaseRef.current = 'idle';
        setFeishuLoginPhase('idle');
      }
      setSession(current);
      void loadBootstrap();
      navigate(defaultPortalPath);
    })().catch(error => setBootstrapError(errorMessage(error)));
  }} />;

  if (!bootstrap && loadingBootstrap) return <div className="platform-portal-boot">
    <Spin loading tip="正在加载授权应用..." />
  </div>;
  if (!bootstrap) return <PortalStatePage variant="system-error"
    onRetry={() => void loadBootstrap()} />;

  let visibleMount = visibleMountIdentityRef.current;
  if (!routedApplication) {
    visibleMountIdentityRef.current = undefined;
    visibleMount = undefined;
  } else if (!visibleMount || visibleMount.appCode !== routedApplication.appCode) {
    const currentAttempt = applicationSwitchAttemptRef.current;
    visibleMount = {
      appCode: routedApplication.appCode,
      mountId: currentAttempt?.targetAppCode === routedApplication.appCode
        ? currentAttempt.targetMountId : crypto.randomUUID(),
      attemptId: currentAttempt?.targetAppCode === routedApplication.appCode
        ? currentAttempt.attemptId : undefined,
      ready: false,
    };
    visibleMountIdentityRef.current = visibleMount;
  }

  let content;
  if (isFixedPortalPath(pathname)) {
    const fixedLeaf = resolveFixedPortalLeaf(pathname);
    if (fixedLeaf?.path === '/portal/todos') {
      const tasksRoute = parseMyTasksRoute(pathname);
      content = <MyTasksPage initialGroup={tasksRoute.group}
        initialTaskId={tasksRoute.taskId}
        applications={applications} identity={bootstrap.identity}
        preparedSelection={preparedTaskSelection}
        navigationLoadingMessage={navigationLoadingMessage}
        onNavigate={navigate} onSessionExpired={recoverSession} />;
    } else if (fixedLeaf?.path === '/portal/drafts') {
      const initialDraftId = pathname.startsWith('/portal/drafts/')
        ? pathname.slice('/portal/drafts/'.length) : undefined;
      content = <DraftsPage initialDraftId={initialDraftId}
        preparedSelection={preparedDraftSelection}
        navigationLoadingMessage={navigationLoadingMessage}
        applications={applications} identity={bootstrap.identity}
        onSessionExpired={recoverSession} onNavigate={navigate} />;
    } else if (fixedLeaf?.path === '/portal/operations') {
      const operationPath = parsePortalOperationPath(pathname);
      content = <PortalOperationListPage
        applications={applications}
        identity={bootstrap.identity}
        applicationCode={operationPath?.applicationCode}
        operationCode={operationPath?.operationCode}
        onNavigate={navigate}
        onSessionExpired={recoverSession}
      />;
    } else if (fixedLeaf?.path === '/portal/workbench') {
      const Workbench = mobile ? MobileWorkbenchPage : PortalWorkbenchPage;
      content = <Workbench applications={applications}
        identity={bootstrap.identity} onNavigate={navigate}
        onSessionExpired={recoverSession} />;
    } else if (fixedLeaf?.path === '/portal/personal') {
      content = <PortalPersonalPage onPasswordChanged={handlePasswordChanged} />;
    } else {
      content = <ConstructionPage title="页面" />;
    }
  } else if (routedApplication) {
    const runtimeState = resolveApplicationRuntimeState(routedApplication.status);
    if (runtimeState === 'maintenance') {
      content = <PortalStatePage variant="maintenance" onBack={() => navigate(defaultPortalPath)} />;
    } else if (runtimeState === 'system-error') {
      content = <PortalStatePage variant="system-error" onBack={() => navigate(defaultPortalPath)} />;
    } else {
      content = <MicroAppHost application={routedApplication} identity={bootstrap.identity}
        pathname={pathname}
        mountId={visibleMount?.mountId}
        switchAttemptId={visibleMount?.attemptId}
        navigate={navigateFromVisibleMicroApp}
        onPrimaryNavigationChange={handlePrimaryNavigationChange}
        onPrimaryNavigationRegister={handleVisiblePrimaryNavigationRegister}
        onLoadReady={handleVisibleLoadReady}
        onLoadFailed={handleVisibleLoadFailed}
        onLoadStageChange={handleVisibleLoadStageChange}
        onSessionExpired={recoverSession} />;
    }
  } else if (isPortalApplicationPath(pathname)) {
    content = <PortalStatePage variant="no-permission" onBack={() => navigate(defaultPortalPath)} />;
  } else {
    content = <PortalStatePage variant="system-error" onBack={() => navigate(defaultPortalPath)} />;
  }

  const Shell = mobile ? MobilePortalEntry : PortalShell;
  const selectMobileApplication = (application: PortalApplication) => {
    if (application.appCode === currentApplication?.appCode && !routedApplication) {
      const remembered = rememberedLocationByApplicationRef.current[application.appCode];
      void navigate(resolveApplicationSwitchPathname(application, remembered?.pathname).pathname);
    } else { void selectApplication(application); }
  };
  return <PortalFormSessionProvider manager={formSessionManager}><MobileApplicationProvider
    applications={applications} pathname={pathname} mobile={mobile} applicationSwitchingTo={switchingApplication?.appName}
    onSelectApplication={selectMobileApplication}><Shell identity={bootstrap.identity} applications={applications}
    currentApplication={currentApplication} pathname={pathname}
    primaryNavigation={primaryNavigation}
    primaryNavigationLoading={Boolean(
      backgroundMount?.status === 'loading',
    )}
    applicationSwitchingTo={switchingApplication?.appName}
    activePrimaryNavigationCode={
      activePrimaryNavigation
      && activePrimaryNavigation.appCode === currentApplication?.appCode
        ? activePrimaryNavigation.navigationCode : undefined
    }
    onNavigate={navigateFromPortalShell}
    onSelectApplication={mobile ? selectMobileApplication : selectApplication}
    onPrimaryNavigationSelect={selectPrimaryNavigation}
    onLogout={logout}>
    {bootstrapError ? <Alert type="warning" content={bootstrapError} /> : null}
    {content}
    {navigationLoadingMessage
      && !['/portal/todos', '/portal/drafts'].includes(resolveFixedPortalLeaf(pathname)?.path ?? '')
      ? <div className="portal-form-navigation-overlay" role="status" aria-live="polite" aria-busy="true">
        <Spin loading tip={navigationLoadingMessage} />
      </div> : null}
    {backgroundApplication && backgroundMount ? <div hidden aria-hidden="true">
      <MicroAppHost
        application={backgroundApplication}
        identity={bootstrap.identity}
        pathname={`${backgroundApplication.entry.basePath}${backgroundApplication.entry.defaultRoute}`}
        mountId={backgroundMount.mountId}
        navigate={path => { void navigate(path); }}
        onPrimaryNavigationChange={ignorePrimaryNavigationChange}
        onPrimaryNavigationRegister={handleCurrentBackgroundPrimaryNavigationRegister}
        onLoadFailed={handleCurrentBackgroundLoadFailed}
        onSessionExpired={recoverSession}
      />
    </div> : null}
  </Shell></MobileApplicationProvider></PortalFormSessionProvider>;
}
